// apps/web/src/layouts/components/SidebarCommissionAttentionBadge.tsx
//
// Badge dinâmica de atenção do financeiro (Comissões > admin). Espelha o
// <SidebarApprovalsBadge> do Pricing: conta as contestações abertas + as
// pendências resolvidas aguardando palavra final do admin (total que pulsa).
//
// O conteúdo vive num componente interno renderizado só quando o usuário tem
// `commissions:manage` — assim a query nem monta para quem não pode gerir
// comissão (mesmo gate do grupo "comissoes" na sidebar admin).

import { useQueryClient } from '@tanstack/react-query';

import { queryKeys, useCommissionAttentionCount, useSocketEvent } from '@alamo/api';
import { usePermissions } from '@alamo/auth';

export interface SidebarCommissionAttentionBadgeProps {
  variant?: 'highlighted' | 'muted';
  ariaLabel?: string;
}

export function SidebarCommissionAttentionBadge({
  variant,
  ariaLabel,
}: SidebarCommissionAttentionBadgeProps) {
  const { hasPermission } = usePermissions();
  if (!hasPermission('commissions', 'manage')) return null;
  return <SidebarCommissionAttentionBadgeInner variant={variant} ariaLabel={ariaLabel} />;
}

function SidebarCommissionAttentionBadgeInner({
  variant = 'highlighted',
  ariaLabel,
}: SidebarCommissionAttentionBadgeProps) {
  const queryClient = useQueryClient();

  // Realtime: o backend emite `commissions.attention.count_changed` (room admin
  // auto-join) quando abre/resolve contestação ou aplica/limpa/resolve pendência.
  // Invalida o contador do badge. useSocketEvent adquire o socket compartilhado,
  // então a assinatura mantém a conexão viva enquanto o badge está montado.
  useSocketEvent('commissions.attention.count_changed', () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.commissionAttention.count() });
  });

  const { data } = useCommissionAttentionCount();

  const total = data?.total ?? 0;
  if (total === 0) return null;

  const style =
    variant === 'highlighted' ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-800';

  // Detalha o tooltip: N contestações abertas + M pendências resolvidas.
  const title =
    ariaLabel ??
    `${data?.openDisputes ?? 0} contestações abertas, ${data?.resolvedPendencies ?? 0} pendências resolvidas a confirmar`;

  return (
    <span
      data-testid="sidebar-commission-attention-badge"
      title={title}
      aria-label={title}
      className={
        'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 ' +
        'text-[10px] font-body font-semibold tabular-nums ' +
        style
      }
    >
      {total > 99 ? '99+' : total}
    </span>
  );
}
